<template>
  <section>
    <div class="d-flex justify-content-between align-items-center">
      <h4>{{ $t('EnduserChat.tab_talks') }}</h4>
      <button class="btn btn-primary btn-sm" @click="downloadCsv" :disabled="csvLoading">
        ⬇ CSV
      </button>
    </div>
    <hr>

    <!-- フィルタ -->
    <div class="row g-2 align-items-end mb-3">
      <div class="col-auto">
        <label class="form-label small text-muted mb-0">{{ $t('EnduserChat.filter_from') }}</label>
        <input type="date" class="form-control form-control-sm" v-model="filters.from">
      </div>
      <div class="col-auto">
        <label class="form-label small text-muted mb-0">{{ $t('EnduserChat.filter_to') }}</label>
        <input type="date" class="form-control form-control-sm" v-model="filters.to">
      </div>
      <div class="col-auto">
        <label class="form-label small text-muted mb-0">{{ $t('EnduserChat.filter_menu') }}</label>
        <select class="form-select form-select-sm" v-model="filters.menu">
          <option value="">{{ $t('EnduserChat.filter_all') }}</option>
          <option v-for="m in menus" :key="m.key" :value="m.key">{{ m.label }}</option>
        </select>
      </div>
      <div class="col-auto">
        <label class="form-label small text-muted mb-0">{{ $t('EnduserChat.filter_status') }}</label>
        <select class="form-select form-select-sm" v-model="filters.status">
          <option value="">{{ $t('EnduserChat.filter_all') }}</option>
          <option value="resolved">{{ $t('EnduserChat.resolved') }}</option>
          <option value="unresolved">{{ $t('EnduserChat.unresolved') }}</option>
          <option value="none">{{ $t('EnduserChat.status_none') }}</option>
        </select>
      </div>
      <div class="col-auto">
        <label class="form-label small text-muted mb-0">{{ $t('EnduserChat.filter_talk_id') }}</label>
        <input type="text" class="form-control form-control-sm" style="min-width: 220px;"
          v-model="filters.talk_id" :placeholder="$t('EnduserChat.filter_talk_id_ph')"
          @keyup.enter="search">
      </div>
      <div class="col-auto">
        <label class="form-label small text-muted mb-0">{{ $t('EnduserChat.filter_keyword') }}</label>
        <input type="text" class="form-control form-control-sm" style="min-width: 220px;"
          v-model="filters.keyword" :placeholder="$t('EnduserChat.filter_keyword_ph')"
          @keyup.enter="search">
      </div>
      <div class="col-auto">
        <button class="btn btn-outline-secondary btn-sm" @click="search">{{ $t('EnduserChat.search') }}</button>
      </div>
    </div>

    <div v-if="loading" class="text-muted">{{ $t('EnduserChat.loading') }}</div>
    <div v-else>
      <table class="table table-hover table-sm align-middle">
        <thead class="table-light">
          <tr>
            <th>{{ $t('EnduserChat.col_datetime') }}</th>
            <th>{{ $t('EnduserChat.col_talk_id') }}</th>
            <th>{{ $t('EnduserChat.col_menu') }}</th>
            <th>{{ $t('EnduserChat.col_user') }}</th>
            <th>{{ $t('EnduserChat.col_first_message') }}</th>
            <th class="text-center">{{ $t('EnduserChat.col_count') }}</th>
            <th class="text-center">{{ $t('EnduserChat.col_status') }}</th>
            <th class="text-end">{{ $t('EnduserChat.col_points') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="t in items" :key="t.id" style="cursor:pointer" @click="openDetail(t.id)">
            <td>{{ fmtDate(t.started_at) }}</td>
            <td><code>{{ t.display_code || '-' }}</code></td>
            <td><span class="badge bg-info-subtle text-dark">{{ menuLabel(t.menu_kind) }}</span></td>
            <td>{{ t.user_display_name }}</td>
            <td class="text-truncate" style="max-width:280px">{{ t.first_message }}</td>
            <td class="text-center">{{ t.message_count }}</td>
            <td class="text-center">
              <span :class="statusBadgeClass(t.resolution)">{{ statusLabel(t.resolution) }}</span>
            </td>
            <td class="text-end">{{ t.point_consumed }}</td>
          </tr>
          <tr v-if="items.length === 0">
            <td colspan="8" class="text-center text-muted py-3">{{ $t('EnduserChat.no_data') }}</td>
          </tr>
        </tbody>
      </table>

      <div class="d-flex justify-content-between align-items-center small text-muted">
        <span>{{ total }} 件</span>
        <div>
          <button class="btn btn-outline-secondary btn-sm" :disabled="page<=1" @click="go(page-1)">‹</button>
          <span class="mx-2">{{ page }} / {{ totalPages }}</span>
          <button class="btn btn-outline-secondary btn-sm" :disabled="page>=totalPages" @click="go(page+1)">›</button>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { defineComponent } from "vue";
import { enduserChatApi } from "@/api";

// 一覧の1ページあたり件数（顧客要望で 100 件）
const DEFAULT_SIZE = 100;

export default defineComponent({
  name: "EnduserChatTalksView",
  data() {
    return {
      loading: true,
      csvLoading: false,
      items: [],
      total: 0,
      page: 1,
      size: DEFAULT_SIZE,
      menus: [],
      filters: { from: "", to: "", menu: "", status: "", talk_id: "", keyword: "" },
    };
  },
  computed: {
    totalPages() {
      return Math.max(1, Math.ceil(this.total / this.size));
    },
  },
  methods: {
    // 解決状態（3値）のラベル・バッジ色。null=空欄（未回答）
    statusLabel(resolution) {
      if (resolution === "resolved") return this.$t("EnduserChat.resolved");
      if (resolution === "unresolved") return this.$t("EnduserChat.unresolved");
      return this.$t("EnduserChat.status_none");
    },
    statusBadgeClass(resolution) {
      if (resolution === "resolved") return "badge bg-success";
      if (resolution === "unresolved") return "badge bg-danger";
      return "badge bg-secondary";
    },
    queryParams() {
      const p = { page: this.page, size: this.size };
      if (this.filters.from) p.from = this.filters.from;
      if (this.filters.to) p.to = this.filters.to;
      if (this.filters.menu) p.menu = this.filters.menu;
      if (this.filters.status !== "") p.status = this.filters.status;
      if (this.filters.talk_id && this.filters.talk_id.trim()) p.talk_id = this.filters.talk_id.trim();
      if (this.filters.keyword && this.filters.keyword.trim()) p.keyword = this.filters.keyword.trim();
      return p;
    },
    async load() {
      // 表示中のページ・フィルタを URL に反映する。ブラウザの「戻る」で一覧に
      // 戻った際も、直前に見ていたページが URL から復元されるようにするため。
      // （履歴を増やさないよう replace。詳細へ遷移する前の履歴が最新状態になる）
      this.$router.replace({ query: this.currentQuery() }).catch(() => {});
      this.loading = true;
      try {
        const data = await enduserChatApi.getTalks(this.queryParams());
        this.items = data.items || [];
        this.total = data.total || 0;
      } catch (e) {
        console.error("failed to load talks", e);
      } finally {
        this.loading = false;
      }
    },
    search() {
      this.page = 1;
      this.load();
    },
    go(p) {
      this.page = p;
      this.load();
    },
    // 現在の一覧状態（ページ・フィルタ）を URL クエリ形式で返す。
    // 詳細へ渡し、詳細の「一覧へ」で戻すことでページング/絞り込みを保持する（No.45）。
    currentQuery() {
      const q = { page: this.page };
      if (this.size !== DEFAULT_SIZE) q.size = this.size;
      if (this.filters.from) q.from = this.filters.from;
      if (this.filters.to) q.to = this.filters.to;
      if (this.filters.menu) q.menu = this.filters.menu;
      if (this.filters.status !== "") q.status = this.filters.status;
      if (this.filters.talk_id && this.filters.talk_id.trim()) q.talk_id = this.filters.talk_id.trim();
      if (this.filters.keyword && this.filters.keyword.trim()) q.keyword = this.filters.keyword.trim();
      return q;
    },
    openDetail(id) {
      this.$router.push({
        name: "EnduserChatTalkDetail",
        params: { talk_id: id },
        query: this.currentQuery(),
      });
    },
    menuLabel(key) {
      const m = this.menus.find(x => x.key === key);
      return m ? m.label : (key || "-");
    },
    fmtDate(iso) {
      if (!iso) return "";
      const d = new Date(iso);
      const p = n => String(n).padStart(2, "0");
      return `${d.getFullYear()}/${p(d.getMonth()+1)}/${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
    },
    async downloadCsv() {
      this.csvLoading = true;
      try {
        const params = {};
        if (this.filters.from) params.from = this.filters.from;
        if (this.filters.to) params.to = this.filters.to;
        const blob = await enduserChatApi.downloadTalksCsv(params);
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "enduser_talks.csv";
        a.click();
        window.URL.revokeObjectURL(url);
      } catch (e) {
        console.error("failed to download csv", e);
      } finally {
        this.csvLoading = false;
      }
    },
    async loadMenus() {
      try {
        const cfg = await enduserChatApi.getConfig();
        this.menus = cfg.menu_definitions || [];
      } catch (e) {
        console.error("failed to load menus", e);
      }
    },
  },
  mounted() {
    // 詳細から戻ってきた際に、ページ・フィルタを URL クエリから復元する（No.45）
    const q = this.$route.query || {};
    if (q.page) this.page = parseInt(q.page, 10) || 1;
    if (q.size) this.size = parseInt(q.size, 10) || this.size;
    this.filters.from = q.from || "";
    this.filters.to = q.to || "";
    this.filters.menu = q.menu || "";
    this.filters.status = q.status != null ? String(q.status) : "";
    this.filters.talk_id = q.talk_id || "";
    this.filters.keyword = q.keyword || "";
    this.loadMenus();
    this.load();
  },
});
</script>
